import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, User, Calendar, MapPin, Tag, Lightbulb, Clock } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { contactLogsApi } from "@/api/contact-logs";
import { suggestionsApi } from "@/api/suggestions";
import type { ContactLog, Suggestion, Contact } from "@/types/api";
import { useToast } from "@/hooks/use-toast";
import { contactsApi } from "@/api/contacts";
import SuggestionCard from "@/components/suggestions/SuggestionCard";
import MemoryCard from "@/components/memories/MemoryCard";
import { MemoryDetailSkeleton } from "@/components/skeletons/MemoryDetailSkeleton";

const MemoryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [memory, setMemory] = useState<ContactLog | null>(null);
  const [contactDetails, setContactDetails] = useState<Contact | null>(null);
  const [relatedSuggestions, setRelatedSuggestions] = useState<Suggestion[]>([]);
  const [relatedMemories, setRelatedMemories] = useState<ContactLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMemoryDetails = async () => {
      if (!id) return;

      setLoading(true);
      try {
        // Fetch and display memory first
        const memoryData = await contactLogsApi.getById(parseInt(id));
        setMemory(memoryData);
        setLoading(false); // Stop showing skeleton, show memory immediately

        // Fetch and display contact details
        if (memoryData.contact) {
          contactsApi.get(memoryData.contact)
            .then(contact => {
              setContactDetails(contact);

              // Fetch related memories with avatar info
              contactLogsApi.list({
                contact: memoryData.contact,
                page_size: 100,
                ordering: '-log_date'
              })
                .then(allMemories => {
                  if (allMemories.results && allMemories.results.length > 0) {
                    // Filter out current memory and limit to 5
                    const otherMemories = allMemories.results.filter(
                      m => m.id !== memoryData.id
                    );

                    // Add contact avatar to each memory
                    const memoriesWithAvatar = otherMemories.map(m => ({
                      ...m,
                      contact_avatar: contact?.avatar
                    }));

                    setRelatedMemories(memoriesWithAvatar.slice(0, 5));
                  }
                })
                .catch(error => {
                  console.error('Error fetching related memories:', error);
                });
            })
            .catch(error => {
              console.error('Error fetching contact details:', error);
            });
        }

        // Fetch and display suggestions related to this memory
        suggestionsApi.list({
          memory_id: parseInt(id),
          ordering: '-priority,-created_at',
          page_size: 10
        })
          .then(memorySuggestions => {
            if (memorySuggestions.results && memorySuggestions.results.length > 0) {
              setRelatedSuggestions(memorySuggestions.results.slice(0, 5));
            }
          })
          .catch(error => {
            console.error('Error fetching suggestions:', error);
          });

      } catch (error) {
        console.error('Error fetching memory details:', error);
        toast({
          title: "Error",
          description: "Failed to load memory details",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    fetchMemoryDetails();
  }, [id, toast]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  if (loading) {
    return (
      <DashboardLayout>
        <MemoryDetailSkeleton />
      </DashboardLayout>
    );
  }

  if (!memory) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 md:px-8">
          <div className="py-6 md:py-8">
            <div className="bg-white rounded-lg p-12 text-center">
              <p className="text-gray-500">Memory not found</p>
              <Button
                onClick={() => navigate('/memories')}
                className="mt-4 bg-[#6C4F77] hover:bg-[#5a4063]"
              >
                Back to Memories
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Memory Card */}
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                {/* Header - Contact Info */}
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => navigate(`/contacts/${memory.contact}`)}
                  >
                    <Avatar className="w-16 h-16">
                      {contactDetails?.avatar ? (
                        <AvatarImage
                          src={`/images/avatars/${contactDetails.avatar}`}
                          alt={memory.contact_name}
                        />
                      ) : null}
                      <AvatarFallback className="bg-gray-200">
                        <User className="w-8 h-8 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-wabisabi-plum transition-colors">
                        {memory.contact_name}
                      </h3>
                      {contactDetails?.relationship_type && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <Tag className="w-3 h-3" />
                          <span className="text-sm capitalize">
                            {contactDetails.relationship_type.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {memory.mood && (
                    <span className="px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-sm">
                      {memory.mood}
                    </span>
                  )}
                </div>

                {/* Memory Content */}
                <div className="mb-6">
                  <p className="text-gray-700 text-lg whitespace-pre-wrap">
                    {memory.content}
                  </p>
                </div>

                {/* Meta Info */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">Date:</span>
                    <span>{formatDate(memory.log_date)}</span>
                  </div>
                  {memory.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">Location:</span>
                      <span>{memory.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">Added:</span>
                    <span>{formatDateTime(memory.created_at)}</span>
                  </div>
                </div>

                {/* Tags */}
                {memory.tags && memory.tags.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Tags:</span>
                      {memory.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestions Section */}
              {relatedSuggestions.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-wabisabi-plum mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    Suggestions
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Based on your memories with {memory.contact_name}, here are some suggestions to help you stay connected:
                  </p>
                  <div className="space-y-3">
                    {relatedSuggestions.map((suggestion) => (
                      <SuggestionCard key={suggestion.id} suggestion={suggestion} showCounts={true} showAvatar={false} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Related Memories */}
            <div className="space-y-6">
              {/* Related Memories */}
              {relatedMemories.length > 0 && (
                <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">More memories with {memory.contact_name}</h3>
                  <div className="space-y-4">
                    {relatedMemories.map((relatedMemory) => (
                      <MemoryCard
                        key={relatedMemory.id}
                        memory={relatedMemory}
                        variant="compact"
                        showAvatar={true}
                      />
                    ))}

                    <Button
                      onClick={() => navigate(`/contacts/${memory.contact}`)}
                      variant="outline"
                      className="w-full mt-2"
                      size="sm"
                    >
                      View All Memories →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MemoryDetail;
